import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Grid, Paper, TextField, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { PackingListService } from '../../services/packingList.service';
import { ExpFormService } from '../../services/expForm.service';
import { BuyerOrderService } from '../../services/buyerOrder.service';
import { CreatePackingListRequest } from '../../interfaces/commercial.interface';
import RefAutocomplete, { RefOption } from './common/RefAutocomplete';
import { fetchAllPages } from './common/fetchAllPages';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';

const today = () => toLocalIsoDate();

const EMPTY = {
    packingListNumber: '', packingDate: today(), expFormId: '', buyerOrderId: '',
    destination: '', remarks: '',
};

const PackingListForm: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { id } = useParams<{ id: string }>();
    const isEdit = !!id;
    const institutionId = AuthService.getSelectedInstitution()!;

    const [form, setForm] = useState({ ...EMPTY });
    const [expFormOptions, setExpFormOptions] = useState<RefOption[]>([]);
    const [buyerOrderOptions, setBuyerOrderOptions] = useState<RefOption[]>([]);
    const [refOptionsLoading, setRefOptionsLoading] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setRefOptionsLoading(true);
            try {
                const [expForms, buyerOrders] = await Promise.all([
                    fetchAllPages((page, size) => ExpFormService.list(institutionId, { page, size })),
                    fetchAllPages((page, size) => BuyerOrderService.list(institutionId, page, size)),
                ]);
                if (cancelled) return;
                setExpFormOptions(expForms.map(f => ({
                    id: f.id, label: f.buyerName ? `${f.expNumber} — ${f.buyerName}` : f.expNumber,
                })));
                setBuyerOrderOptions(buyerOrders.map(o => ({
                    id: o.id, label: o.orderDate ? `${o.orderNumber} — ${formatDate(o.orderDate)}` : o.orderNumber,
                })));
            } catch (e) {
                if (!cancelled) toast.showApiError(e, 'Failed to load EXP form / buyer order options');
            } finally {
                if (!cancelled) setRefOptionsLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                setLoading(true);
                const pl = await PackingListService.getById(institutionId, id);
                setForm({
                    packingListNumber: pl.packingListNumber, packingDate: pl.packingDate,
                    expFormId: pl.expFormId ?? '', buyerOrderId: pl.buyerOrderId ?? '',
                    destination: pl.destination ?? '', remarks: pl.remarks ?? '',
                });
            } catch (e) {
                toast.showApiError(e, 'Failed to load packing list');
            } finally {
                setLoading(false);
            }
        })();
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm(f => ({ ...f, [k]: e.target.value }));

    const validate = (): string | null => {
        if (!form.packingListNumber.trim()) return 'Packing list number is required.';
        if (!form.packingDate) return 'Packing date is required.';
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            return;
        }
        setError(null);

        const dto: CreatePackingListRequest = {
            packingListNumber: form.packingListNumber.trim(),
            packingDate: form.packingDate,
            expFormId: form.expFormId.trim() || undefined,
            buyerOrderId: form.buyerOrderId.trim() || undefined,
            destination: form.destination.trim() || undefined,
            remarks: form.remarks.trim() || undefined,
        };

        try {
            setSaving(true);
            const saved = isEdit
                ? await PackingListService.update(institutionId, id!, dto)
                : await PackingListService.create(institutionId, dto);
            toast.showSuccess(isEdit ? 'Packing list updated' : 'Packing list created');
            navigate(`/commercial/packing-lists/${saved.id}`);
        } catch (err) {
            toast.showApiError(err, isEdit ? 'Failed to update packing list' : 'Failed to create packing list');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 3, maxWidth: 900 }} data-help-page="commercial.plForm.page">
            <Typography variant="h5" sx={{ mb: 2 }}>{isEdit ? 'Edit Packing List' : 'New Packing List'}</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Paper sx={{ p: 3 }} data-help-section="commercial.plForm.section.details">
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.plForm.field.packingListNumber" label="Packing List Number" fullWidth required value={form.packingListNumber} onChange={set('packingListNumber')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField data-help="commercial.plForm.field.packingDate" label="Packing Date" type="date" fullWidth required InputLabelProps={{ shrink: true }}
                                value={form.packingDate} onChange={set('packingDate')} />
                        </Grid>
                        <Grid size={12}>
                            <TextField data-help="commercial.plForm.field.destination" label="Destination" fullWidth value={form.destination} onChange={set('destination')} />
                        </Grid>

                        <Grid size={12}><Typography variant="subtitle2">Linked documents</Typography></Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <RefAutocomplete
                                data-help="commercial.plForm.field.expForm"
                                label="EXP Form"
                                options={expFormOptions}
                                loading={refOptionsLoading}
                                value={form.expFormId || null}
                                onChange={(newId) => setForm(f => ({ ...f, expFormId: newId ?? '' }))}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <RefAutocomplete
                                data-help="commercial.plForm.field.buyerOrder"
                                label="Buyer Order"
                                options={buyerOrderOptions}
                                loading={refOptionsLoading}
                                value={form.buyerOrderId || null}
                                onChange={(newId) => setForm(f => ({ ...f, buyerOrderId: newId ?? '' }))}
                            />
                        </Grid>

                        <Grid size={12}>
                            <TextField data-help="commercial.plForm.field.remarks" label="Remarks" fullWidth multiline rows={3} value={form.remarks} onChange={set('remarks')} />
                        </Grid>

                        <Grid size={12} sx={{ display: 'flex', gap: 1 }}>
                            <Button data-help="commercial.plForm.save" type="submit" variant="contained" disabled={saving}>
                                {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Packing List'}
                            </Button>
                            <Button data-help="commercial.plForm.cancel" variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
        </Box>
    );
};

export default PackingListForm;
